import React, { useEffect, useMemo, useRef, useState } from 'react';
import { crearComparacion, fetchComparacion, fetchRun, guardarReferencia, listarComparaciones, listarReferencias, listarRuns } from './api';
import { coincidenciaConReferencia, comparacionTerminada, indicePorCaso, interseccionDeCasos, mismosRunIds, textoCoincidencia } from './comparacion';
import FilaCasoComparar from './FilaCasoComparar';
import { casosQueVencenPrimero } from './firmas';
import { corridaEnCurso, resumenPorTipoCaso } from './metricas';
import PanelComparacionIA from './PanelComparacionIA';
import ResultadoComparacionIA from './ResultadoComparacionIA';
import ResumenMetricas from './ResumenMetricas';
import SeleccionCorridas from './SeleccionCorridas';
import type { Accion, ComparacionIA, ConfigAnalisis, Referencia, RunDetalle, RunResumen, TipoCaso } from './tipos';
import { Aviso } from './ui';
import { useRefrescoFirmas } from './useRefrescoFirmas';

const MAX_CORRIDAS = 8;
/**
 * Los detalles traen URLs firmadas (1 h): se cachean por sesión y se re-piden pasado este
 * tiempo al cambiar la selección. La renovación en vivo la hace useRefrescoFirmas; esto
 * queda como red para una corrida que salió de la selección y vuelve más tarde.
 */
const VIDA_CACHE_MS = 50 * 60 * 1000;

/**
 * Sub-vista Comparar: N corridas (2–8) sobre la intersección de sus casos, contra el
 * análisis humano por imagen ("Mi análisis", independiente de la corrida) y, a pedido,
 * la lectura de un modelo que compara ambos (`POST /comparaciones`, poll cada 3 s).
 * Las métricas de arriba se calculan SOLO sobre la intersección, así todas las columnas
 * hablan del mismo conjunto.
 */
const AnalisisComparar: React.FC<{ activa: boolean; config: ConfigAnalisis }> = ({ activa, config }) => {
  const [runs, setRuns] = useState<RunResumen[]>([]);
  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [detalles, setDetalles] = useState<Map<string, RunDetalle>>(new Map());
  const cacheRef = useRef(new Map<string, { detalle: RunDetalle; at: number }>());
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referencias, setReferencias] = useState<Map<string, Referencia>>(new Map());
  const [comparaciones, setComparaciones] = useState<ComparacionIA[]>([]);
  const [comparacion, setComparacion] = useState<ComparacionIA | null>(null);
  const [modeloIA, setModeloIA] = useState(config.modelos_comparacion[0] ?? '');
  const [lanzando, setLanzando] = useState(false);
  const [errorIA, setErrorIA] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const recargarComparaciones = async () => {
    try {
      setComparaciones(await listarComparaciones());
    } catch (e) {
      console.error('[analisis] listar comparaciones falló:', e);
      setErrorIA(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    if (!activa) return;
    listarRuns()
      .then((lista) => {
        setRuns(lista);
        setError(null);
      })
      .catch((e) => {
        console.error('[analisis] listar corridas (comparar) falló:', e);
        setError(e instanceof Error ? e.message : String(e));
      });
    void recargarComparaciones();
  }, [activa]);

  // Mientras haya corridas en curso, la lista se refresca cada 5 s: así una que termina pasa
  // a ser elegible sin recargar (SeleccionCorridas bloquea las que siguen en curso — el
  // detalle se cachea al elegir y comparar resultados parciales mezclaría conjuntos).
  const hayEnCurso = runs.some(corridaEnCurso);
  useEffect(() => {
    if (!activa || !hayEnCurso) return;
    const timer = setInterval(() => {
      listarRuns()
        .then(setRuns)
        .catch((e) => console.error('[analisis] refrescar corridas (comparar) falló:', e));
    }, 5000);
    return () => clearInterval(timer);
  }, [activa, hayEnCurso]);

  // Detalles de las corridas elegidas (con caché): las tarjetas salen de la primera.
  useEffect(() => {
    setComparacion(null);
    const ahora = Date.now();
    const faltan = seleccion.filter((id) => {
      const c = cacheRef.current.get(id);
      return !c || ahora - c.at > VIDA_CACHE_MS;
    });
    const armar = () => new Map(seleccion.flatMap((id) => (cacheRef.current.get(id) ? [[id, cacheRef.current.get(id)!.detalle] as const] : [])));
    if (faltan.length === 0) {
      setDetalles(armar());
      return;
    }
    let vivo = true;
    setCargando(true);
    setError(null);
    Promise.all(faltan.map((id) => fetchRun(id)))
      .then((ds) => {
        if (!vivo) return;
        for (const d of ds) cacheRef.current.set(d.run.run_id, { detalle: d, at: Date.now() });
        setDetalles(armar());
      })
      .catch((e) => {
        console.error('[analisis] detalles para comparar fallaron:', e);
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, [seleccion]);

  const corridas = useMemo(() => seleccion.map((id) => detalles.get(id)).filter((d): d is RunDetalle => !!d), [seleccion, detalles]);

  // URLs firmadas: cada corrida se pidió cuando se la agregó, así que manda la que vence
  // PRIMERO y se renuevan todas juntas (detalle entero: `referencias` y la comparación son
  // estado aparte, y "Mi análisis" vive en cada fila por imagen_id, así que nada se pisa).
  const casosParaFirmas = useMemo(() => casosQueVencenPrimero(corridas.map((d) => d.casos)), [corridas]);
  const refrescarFirmas = async () => {
    const ids = seleccion.filter((id) => cacheRef.current.has(id));
    if (ids.length === 0) return;
    const ds = await Promise.all(ids.map((id) => fetchRun(id)));
    for (const d of ds) cacheRef.current.set(d.run.run_id, { detalle: d, at: Date.now() });
    setDetalles((prev) => {
      const m = new Map(prev);
      for (const d of ds) if (m.has(d.run.run_id)) m.set(d.run.run_id, d);
      return m;
    });
  };
  useRefrescoFirmas(casosParaFirmas, refrescarFirmas);
  const listas = seleccion.length >= 2 && corridas.length === seleccion.length;
  const casos = useMemo(() => (listas ? interseccionDeCasos(corridas) : []), [corridas, listas]);
  const idsCasos = useMemo(() => casos.map((c) => c.imagen_id), [casos]);
  const claveIds = idsCasos.join(',');

  // Referencias humanas de la intersección (por imagen, no por corrida).
  useEffect(() => {
    if (!claveIds) {
      setReferencias(new Map());
      return;
    }
    let vivo = true;
    listarReferencias(claveIds.split(','))
      .then((refs) => {
        if (vivo) setReferencias(new Map(refs.map((r) => [r.imagen_id, r])));
      })
      .catch((e) => {
        console.error('[analisis] listar referencias falló:', e);
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [claveIds]);

  // Poll de la comparación en curso cada 3 s hasta `finished_at`.
  useEffect(() => {
    if (!comparacion || comparacionTerminada(comparacion)) return;
    const timer = setTimeout(() => {
      fetchComparacion(comparacion.id)
        .then((c) => {
          setComparacion((prev) => (prev && prev.id === c.id ? c : prev));
          if (comparacionTerminada(c)) void recargarComparaciones();
        })
        .catch((e) => {
          console.error('[analisis] poll de comparación falló:', e);
          setErrorIA(`El poll falló (se reintenta en 3 s): ${e instanceof Error ? e.message : String(e)}`);
        })
        .finally(() => setTick((t) => t + 1));
    }, 3000);
    return () => clearTimeout(timer);
  }, [comparacion, tick]);

  const guardarRef = async (imagenId: string, accion: Accion | null, texto: string) => {
    const r = await guardarReferencia(imagenId, accion, texto);
    setReferencias((prev) => {
      const m = new Map(prev);
      if (r) m.set(imagenId, r);
      else m.delete(imagenId);
      return m;
    });
  };

  const lanzar = async () => {
    setLanzando(true);
    setErrorIA(null);
    try {
      const creada = await crearComparacion(seleccion, modeloIA || undefined, idsCasos);
      setComparacion(await fetchComparacion(creada.comparacion_id));
    } catch (e) {
      console.error('[analisis] comparar con IA falló:', e);
      setErrorIA(e instanceof Error ? e.message : String(e));
    } finally {
      setLanzando(false);
    }
  };

  const abrir = async (id: string) => {
    setErrorIA(null);
    try {
      setComparacion(await fetchComparacion(id));
    } catch (e) {
      console.error('[analisis] abrir comparación falló:', e);
      setErrorIA(e instanceof Error ? e.message : String(e));
    }
  };

  const etiquetaDe = (runId: string) => {
    const i = seleccion.indexOf(runId);
    const r = detalles.get(runId)?.run ?? runs.find((x) => x.run_id === runId);
    return `${i >= 0 ? `#${i + 1} ` : ''}${r ? `${r.etiqueta} · ${r.modelo}` : runId.slice(0, 8)}`;
  };

  const idsSet = useMemo(() => new Set(idsCasos), [idsCasos]);
  const tabla = (tipo: TipoCaso, titulo: string) => (
    <ResumenMetricas
      titulo={titulo}
      columnas={corridas.map((d, i) => ({
        titulo: `#${i + 1} ${d.run.etiqueta}`,
        resumen: resumenPorTipoCaso(d.resultados.filter((r) => idsSet.has(r.imagen_id)), config.fidelidad_max)[tipo],
      }))}
      extra={[
        {
          etiqueta: 'Coincide con mi análisis',
          valores: corridas.map((d) =>
            textoCoincidencia(coincidenciaConReferencia(d.resultados.filter((r) => idsSet.has(r.imagen_id) && r.tipo_caso === tipo), referencias)),
          ),
        },
      ]}
    />
  );

  const lecturas = useMemo(() => indicePorCaso(comparacion?.resultado?.ia), [comparacion]);
  const anteriores = comparaciones.filter((c) => mismosRunIds(c.run_ids, seleccion));
  const conReferencia = idsCasos.filter((id) => referencias.has(id)).length;

  return (
    <div className="space-y-6">
      <SeleccionCorridas runs={runs} seleccion={seleccion} max={MAX_CORRIDAS} onCambiar={setSeleccion} />
      <Aviso error={error} />
      {cargando && <p className="text-sm text-gray-500">Cargando corridas…</p>}
      {seleccion.length === 1 && <p className="text-sm text-gray-500">Elegí al menos una corrida más.</p>}
      {listas && casos.length === 0 && <Aviso error="Estas corridas no tienen casos en común: no hay nada que comparar." />}

      {listas && casos.length > 0 && (
        <>
          <PanelComparacionIA
            modelos={config.modelos_comparacion}
            modelo={modeloIA}
            onModelo={setModeloIA}
            casosTotal={casos.length}
            casosConReferencia={conReferencia}
            lanzando={lanzando}
            onLanzar={() => void lanzar()}
            comparacion={comparacion}
            anteriores={anteriores}
            onAbrir={(id) => void abrir(id)}
          />
          <Aviso error={errorIA} />
          {comparacion && <ResultadoComparacionIA comparacion={comparacion} etiquetaDe={etiquetaDe} />}

          <div className="grid gap-4 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-2">
            {tabla('editada', `Editadas (rehacer esperado) · ${casos.filter((c) => c.tipo_caso === 'editada').length} en común`)}
            {tabla('control', `Control (rehacer = falso positivo) · ${casos.filter((c) => c.tipo_caso === 'control').length} en común`)}
          </div>

          <div className="space-y-3">
            {casos.map((c) => (
              <FilaCasoComparar
                key={c.imagen_id}
                caso={c}
                corridas={corridas}
                referencia={referencias.get(c.imagen_id)}
                lecturas={lecturas.get(c.imagen_id)}
                fidelidadMax={config.fidelidad_max}
                onGuardarReferencia={(accion, texto) => guardarRef(c.imagen_id, accion, texto)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalisisComparar;
